import React, { useState, useRef } from 'react';
import { Upload, Link, Image as ImageIcon, Palette, X, Download, Loader, AlertCircle, CheckCircle, Eye, Globe, Monitor, Camera } from 'lucide-react';
import toast from 'react-hot-toast';
import ColorThief from 'colorthief';
import { ColorPalette } from '../types/color';

interface ImageColorExtractorProps {
  onPaletteExtracted: (palette: ColorPalette) => void;
}

export const ImageColorExtractor: React.FC<ImageColorExtractorProps> = ({ onPaletteExtracted }) => {
  const [isExtracting, setIsExtracting] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extractedImage, setExtractedImage] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showWebsiteInput, setShowWebsiteInput] = useState(false);
  const [extractionSource, setExtractionSource] = useState<'file' | 'url' | 'website' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert RGB to HSL
  const rgbToHsl = (r: number, g: number, b: number): string => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
  };

  // Extract colors from image
  const extractColorsFromImage = async (imageElement: HTMLImageElement): Promise<ColorPalette> => {
    return new Promise((resolve, reject) => {
      try {
        const colorThief = new ColorThief();
        
        // Get dominant color and palette
        const dominantColor = colorThief.getColor(imageElement);
        const palette = colorThief.getPalette(imageElement, 5);
        
        // Ensure we have 5 colors (pad with variations if needed)
        const colors: number[][] = [];
        
        if (palette && palette.length > 0) {
          colors.push(...palette.slice(0, 5));
        } else {
          colors.push(dominantColor);
        }
        
        // Fill remaining slots with variations of existing colors
        while (colors.length < 5) {
          const baseColor = colors[colors.length % colors.length];
          const variation = [
            Math.max(0, Math.min(255, baseColor[0] + (Math.random() - 0.5) * 60)),
            Math.max(0, Math.min(255, baseColor[1] + (Math.random() - 0.5) * 60)),
            Math.max(0, Math.min(255, baseColor[2] + (Math.random() - 0.5) * 60))
          ];
          colors.push(variation);
        }
        
        // Convert to HSL
        const hslColors = colors.map(([r, g, b]) => rgbToHsl(r, g, b));
        
        resolve({
          colors: hslColors,
          names: ['Dominant', 'Secondary', 'Accent', 'Supporting', 'Neutral']
        });
      } catch (error) {
        reject(error);
      }
    });
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file', {
        icon: '🖼️',
        duration: 3000,
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB', {
        icon: '📏',
        duration: 3000,
      });
      return;
    }

    setIsExtracting(true);
    setExtractionSource('file');
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageSrc = e.target?.result as string;
        setExtractedImage(imageSrc);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = async () => {
          try {
            const palette = await extractColorsFromImage(img);
            onPaletteExtracted(palette);
            toast.success('Colors extracted from uploaded image!', {
              icon: '🎨',
              duration: 3000,
            });
          } catch (error) {
            console.error('Color extraction failed:', error);
            toast.error('Failed to extract colors from image', {
              icon: '❌',
              duration: 3000,
            });
          } finally {
            setIsExtracting(false);
          }
        };
        img.onerror = () => {
          toast.error('Failed to load image', {
            icon: '🖼️',
            duration: 3000,
          });
          setIsExtracting(false);
        };
        img.src = imageSrc;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File reading failed:', error);
      toast.error('Failed to read image file', {
        icon: '📁',
        duration: 3000,
      });
      setIsExtracting(false);
    }
  };

  // Handle URL input
  const handleUrlExtraction = async () => {
    if (!imageUrl.trim()) {
      toast.error('Please enter a valid image URL', {
        icon: '🔗',
        duration: 3000,
      });
      return;
    }

    setIsExtracting(true);
    setExtractionSource('url');
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = async () => {
        try {
          setExtractedImage(imageUrl);
          const palette = await extractColorsFromImage(img);
          onPaletteExtracted(palette);
          toast.success('Colors extracted from image URL!', {
            icon: '🌐',
            duration: 3000,
          });
          setShowUrlInput(false);
          setImageUrl('');
        } catch (error) {
          console.error('Color extraction failed:', error);
          toast.error('Failed to extract colors from image', {
            icon: '❌',
            duration: 3000,
          });
        } finally {
          setIsExtracting(false);
        }
      };
      
      img.onerror = () => {
        toast.error('Failed to load image from URL. Check CORS policy.', {
          icon: '🚫',
          duration: 4000,
        });
        setIsExtracting(false);
      };
      
      img.src = imageUrl;
    } catch (error) {
      console.error('URL loading failed:', error);
      toast.error('Invalid image URL', {
        icon: '🔗',
        duration: 3000,
      });
      setIsExtracting(false);
    }
  };

  // Handle website screenshot and color extraction using multiple methods
  const handleWebsiteExtraction = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a valid website URL', {
        icon: '🌐',
        duration: 3000,
      });
      return;
    }

    // Add protocol if missing
    let url = websiteUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setIsExtracting(true);
    setExtractionSource('website');

    // Try multiple screenshot services for better reliability
    const screenshotServices = [
      {
        name: 'ScreenshotAPI',
        url: `https://shot.screenshotapi.net/screenshot?token=demo&url=${encodeURIComponent(url)}&width=1200&height=800&file_type=png&wait_for_event=load`,
        timeout: 10000
      },
      {
        name: 'Microlink',
        // Correct Microlink API implementation according to their docs
        url: `https://api.microlink.io/screenshot?url=${encodeURIComponent(url)}&type=png&viewport.width=1200&viewport.height=800&fullPage=false&omitBackground=false`,
        timeout: 12000
      },
      {
        name: 'PagePeeker',
        url: `https://free.pagepeeker.com/v2/thumbs.php?size=l&url=${encodeURIComponent(url)}`,
        timeout: 8000
      }
    ];

    let success = false;

    for (const service of screenshotServices) {
      if (success) break;

      try {
        toast(`Trying ${service.name}...`, {
          icon: '📸',
          duration: 2000,
        });

        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const imagePromise = new Promise<void>((resolve, reject) => {
          img.onload = async () => {
            try {
              setExtractedImage(service.url);
              const palette = await extractColorsFromImage(img);
              onPaletteExtracted(palette);
              
              toast.success(`Colors extracted from website using ${service.name}!`, {
                icon: '🌍',
                duration: 3000,
              });
              
              setShowWebsiteInput(false);
              setWebsiteUrl('');
              success = true;
              resolve();
            } catch (error) {
              reject(error);
            }
          };

          img.onerror = () => {
            reject(new Error(`${service.name} failed to load screenshot`));
          };
        });

        // Set timeout for each service
        const timeoutPromise = new Promise<void>((_, reject) => {
          setTimeout(() => reject(new Error(`${service.name} timeout`)), service.timeout);
        });

        img.src = service.url;
        
        await Promise.race([imagePromise, timeoutPromise]);
        
        if (success) break;

      } catch (error) {
        console.warn(`${service.name} failed:`, error);
        continue;
      }
    }

    if (!success) {
      // Final fallback: Try to extract from favicon or suggest alternatives
      toast.error('Website screenshot failed. Try these alternatives:', {
        icon: '💡',
        duration: 6000,
      });
      
      // Show helpful suggestions
      setTimeout(() => {
        toast('💡 Try: 1) Direct image URLs 2) Screenshots from your device 3) Popular image hosting sites', {
          duration: 8000,
        });
      }, 1000);
    }

    setIsExtracting(false);
  };

  // Clear extracted image
  const clearImage = () => {
    setExtractedImage(null);
    setImageUrl('');
    setWebsiteUrl('');
    setShowUrlInput(false);
    setShowWebsiteInput(false);
    setExtractionSource(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getSourceIcon = () => {
    switch (extractionSource) {
      case 'file': return <Upload size={16} />;
      case 'url': return <Link size={16} />;
      case 'website': return <Globe size={16} />;
      default: return <CheckCircle size={16} />;
    }
  };

  const getSourceText = () => {
    switch (extractionSource) {
      case 'file': return 'Colors extracted from uploaded file!';
      case 'url': return 'Colors extracted from image URL!';
      case 'website': return 'Colors extracted from website!';
      default: return 'Colors extracted successfully!';
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
          <ImageIcon className="text-white" size={16} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Extract Colors from Images & Websites</h3>
          <p className="text-sm text-gray-600">Upload images, use URLs, or capture website screenshots to extract color palettes</p>
        </div>
      </div>

      {/* Upload Options */}
      <div className="space-y-4">
        {!extractedImage ? (
          <>
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isExtracting}
              />
              <div className="space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                  <Upload className="text-gray-400" size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-2">Upload Image</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Drag and drop an image here, or click to browse
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isExtracting}
                    className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 flex items-center space-x-2 mx-auto disabled:opacity-50"
                  >
                    {isExtracting && extractionSource === 'file' ? (
                      <>
                        <Loader className="animate-spin" size={16} />
                        <span>Extracting...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Choose File</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Alternative Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* URL Input Toggle */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowUrlInput(!showUrlInput);
                    setShowWebsiteInput(false);
                  }}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-gray-600 hover:text-blue-600 flex items-center justify-center space-x-2"
                  disabled={isExtracting}
                >
                  <Link size={20} />
                  <span className="font-medium">Image URL</span>
                </button>
              </div>

              {/* Website Input Toggle */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowWebsiteInput(!showWebsiteInput);
                    setShowUrlInput(false);
                  }}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-gray-600 hover:text-green-600 flex items-center justify-center space-x-2"
                  disabled={isExtracting}
                >
                  <Globe size={20} />
                  <span className="font-medium">Website</span>
                </button>
              </div>
            </div>

            {/* URL Input */}
            {showUrlInput && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-800 flex items-center space-x-2">
                  <Link size={16} />
                  <span>Extract from Image URL</span>
                </h5>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isExtracting}
                  />
                  <button
                    onClick={handleUrlExtraction}
                    disabled={isExtracting || !imageUrl.trim()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {isExtracting && extractionSource === 'url' ? (
                      <Loader className="animate-spin" size={16} />
                    ) : (
                      <Download size={16} />
                    )}
                    <span className="hidden sm:inline">Extract</span>
                  </button>
                </div>
                <div className="flex items-start space-x-2 text-xs text-blue-600">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <p>
                    Some images may not work due to CORS restrictions. For best results, use images from the same domain.
                  </p>
                </div>
              </div>
            )}

            {/* Website Input */}
            {showWebsiteInput && (
              <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <h5 className="font-medium text-green-800 flex items-center space-x-2">
                  <Globe size={16} />
                  <span>Extract from Website</span>
                </h5>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="example.com or https://example.com"
                    className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={isExtracting}
                  />
                  <button
                    onClick={handleWebsiteExtraction}
                    disabled={isExtracting || !websiteUrl.trim()}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center space-x-2"
                  >
                    {isExtracting && extractionSource === 'website' ? (
                      <Loader className="animate-spin" size={16} />
                    ) : (
                      <Camera size={16} />
                    )}
                    <span className="hidden sm:inline">Capture</span>
                  </button>
                </div>
                <div className="flex items-start space-x-2 text-xs text-green-600">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <p>
                    Website screenshots try multiple services for better reliability. May take 10-15 seconds.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Extracted Image Preview */
          <div className="space-y-4">
            <div className="relative">
              <img
                src={extractedImage}
                alt="Extracted colors source"
                className="w-full h-48 object-cover rounded-lg shadow-md"
              />
              <button
                onClick={clearImage}
                className="absolute top-2 right-2 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-all"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-2">
                {getSourceIcon()}
                <span className="text-sm font-medium text-green-800">{getSourceText()}</span>
              </div>
              <button
                onClick={clearImage}
                className="text-sm text-green-700 hover:text-green-900 flex items-center space-x-1"
              >
                <Upload size={14} />
                <span>Extract New</span>
              </button>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h5 className="font-semibold text-blue-800 mb-2 flex items-center space-x-2">
            <Eye className="text-blue-600" size={16} />
            <span>Tips for Best Results</span>
          </h5>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Images:</strong> Use high-quality images with clear, distinct colors</li>
            <li>• <strong>URLs:</strong> Ensure images allow cross-origin access (CORS)</li>
            <li>• <strong>Websites:</strong> Multiple screenshot services tried automatically</li>
            <li>• <strong>Formats:</strong> JPG, PNG, GIF, WebP supported (max 10MB for uploads)</li>
            <li>• <strong>Alternative:</strong> Take screenshots manually if website capture fails</li>
          </ul>
        </div>
      </div>
    </div>
  );
};